/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { getUserDetail } from './Api';
import sjcl from '../utils/sjcl.js';
import axios from 'axios';
import Spinner from '../components/Spinner';
import errorLogger from '../utils/ErrorLogger';

const serviceauthId = process.env.REACT_APP_SERVICEAUTH_OAUTH_PUBLIC_CLIENT_ID;
const serviceauthRedirectUri = process.env.REACT_APP_SERVICEAUTH_REDIRECT_URI;
const serviceauthOauthUrl =
  'https://weblogin.asu.edu/serviceauth/oauth2/native/allow';
const serviceauthTokenUrl =
  'https://weblogin.asu.edu/serviceauth/oauth2/native/token';
const serviceauthRefreshTokenUrl =
  'https://weblogin.asu.edu/serviceauth/oauth2/token';
const LOGOUT_URL = 'https://weblogin.asu.edu/cas/logout';
const SS_SA_CODE_VERIFIER = 'syllabus-app.serviceauth.codeVerifier';
const SS_SA_STATE = 'syllabus-app.serviceauth.state';
const SS_JWT_TOKEN = 'syllabus-app.jwt.token';
const SS_JWT_REFRESH_TOKEN = 'syllabus-app.jwt.refresh_token';
const SS_USER = 'syllabus-app.user';
const SS_USER_NAME = 'syllabus-app.user_name';
const SS_JWT_EXPIRATION = 'syllabus-app.jwt.expiration';
const TOKEN_EXPIRES_IN_SECONDS = 540;
const scopes = [process.env.REACT_APP_SERVICEAUTH_APP_SCOPE];

const clearAuthData = (preserveOriginalUrl = false) => {
  localStorage.removeItem(SS_JWT_TOKEN);
  localStorage.removeItem(SS_JWT_REFRESH_TOKEN);
  localStorage.removeItem(SS_JWT_EXPIRATION);
  localStorage.removeItem(SS_USER);
  localStorage.removeItem(SS_USER_NAME);
  localStorage.removeItem(SS_SA_CODE_VERIFIER);
  localStorage.removeItem(SS_SA_STATE);
  localStorage.removeItem('processed_oauth_code'); // Clean up OAuth processing marker
  if (!preserveOriginalUrl) {
    localStorage.removeItem('originalUrl');
  }
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'INIT':
      return { ...state, loading: true, error: null };
    case 'SUCCESS':
      return { loading: false, error: null, user: action.user };
    case 'ERROR':
      return { loading: false, error: action.error, user: null };
    case 'SET_USER':
      return { ...state, user: action.user, loading: false };
    default:
      return state;
  }
};

export const Auth = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    error: null,
    loading: true,
  });

  const retryCountRef = useRef(0);
  const authenticationInProgressRef = useRef(false);

  const authenticate = useCallback(async () => {
    // Prevent multiple simultaneous authentication attempts
    if (authenticationInProgressRef.current) {
      errorLogger.logWarning('Authentication already in progress, skipping', 'auth');
      return;
    }

    authenticationInProgressRef.current = true;
    dispatch({ type: 'INIT' });

    errorLogger.logInfo('Starting authentication process', 'auth', {
      attempt: retryCountRef.current + 1,
      hasStoredToken: !!localStorage.getItem(SS_JWT_TOKEN),
      currentUrl: window.location.href
    });

    try {
      // Handle OAuth callback
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('code') && urlParams.has('state')) {
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const storedState = localStorage.getItem(SS_SA_STATE);

        errorLogger.logInfo('Processing OAuth callback', 'auth', {
          hasCode: !!code,
          hasState: !!state,
          stateMatches: storedState === state
        });

        if (!state || state.length < 1 || storedState !== state) {
          const error = new Error(`Invalid state parameter`);
          errorLogger.logError(error, 'auth', {
            receivedState: state,
            storedState: storedState
          });
          throw error;
        }

        // Check if we've already processed this code to prevent double processing
        const processedCode = localStorage.getItem('processed_oauth_code');
        if (processedCode === code) {
          errorLogger.logWarning('OAuth code already processed, skipping', 'auth', {
            code: code.substring(0, 10) + '...'
          });
          authenticationInProgressRef.current = false;
          return;
        }

        // Mark this code as being processed
        localStorage.setItem('processed_oauth_code', code);

        const { access_token, refresh_token } = await requestTokens(
          code,
          state
        );

        if (access_token && refresh_token) {
          saveTokenWithExpiration(access_token);
          saveRefreshToken(refresh_token);

          // Clean up the processed code marker
          localStorage.removeItem('processed_oauth_code');

          const originalUrl = localStorage.getItem('originalUrl');

          // If we have an original URL, redirect there
          if (originalUrl) {
            // Only remove originalUrl right before redirect to prevent race conditions
            localStorage.removeItem('originalUrl');
            window.location.replace(originalUrl);
          } else {
            // No original URL saved, clean up current URL and go to home
            const url = new URL(window.location);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            window.history.replaceState(
              {},
              document.title,
              url.pathname + url.search
            );
            window.location.replace('/');
          }
          return;
        } else {
          throw new Error('Failed to receive tokens');
        }
      }

      // Check for token
      const sessionToken = await getTokenAsync();

      if (!sessionToken) {
        // Save the current URL so we can redirect back after authentication
        // Only save if we're not already in an OAuth callback
        if (!urlParams.has('code')) {
          localStorage.setItem('originalUrl', window.location.href);
        }
        window.location.replace(redirectToServiceAuth());
        return;
      }

      // Load cached user data first
      const savedUser = localStorage.getItem(SS_USER);
      if (savedUser) {
        dispatch({ type: 'SET_USER', user: JSON.parse(savedUser) });
      }

      // Fetch fresh user data
      try {
        const userDetails = await getUserDetail();
        if (userDetails) {
          saveUserDetails(userDetails);
          setHeader(userDetails);
          dispatch({ type: 'SUCCESS', user: userDetails });
          retryCountRef.current = 0; // Reset on success
          authenticationInProgressRef.current = false; // Reset flag on success
        }
      } catch (err) {
        const newToken = await getTokenAsync();
        if (newToken) {
          try {
            const userDetails = await getUserDetail();
            if (userDetails) {
              saveUserDetails(userDetails);
              setHeader(userDetails);
              dispatch({ type: 'SUCCESS', user: userDetails });
              retryCountRef.current = 0; // Reset on success
              authenticationInProgressRef.current = false; // Reset flag on success
            }
          } catch (retryErr) {
            // Save the current URL before clearing auth data
            localStorage.setItem('originalUrl', window.location.href);
            clearAuthData(true); // Preserve originalUrl
            window.location.replace(redirectToServiceAuth());
            return;
          }
        } else {
          // Save the current URL before clearing auth data
          errorLogger.logInfo('No valid token found, redirecting to ServiceAuth', 'auth', {
            currentUrl: window.location.href
          });
          
          localStorage.setItem('originalUrl', window.location.href);
          clearAuthData(true); // Preserve originalUrl
          window.location.replace(redirectToServiceAuth());
          return;
        }
      }
    } catch (err) {
      console.error(err);
      
      errorLogger.logError(err, 'auth', {
        attempt: retryCountRef.current + 1,
        maxRetries: 3,
        currentUrl: window.location.href
      });

      if (retryCountRef.current === 0) {
        clearAuthData(true); // Preserve originalUrl for potential retry
      }

      dispatch({
        type: 'ERROR',
        error: 'Authentication error. Please try again.',
      });

      if (retryCountRef.current < 3) {
        const delay = retryCountRef.current * 1000; // 0s, 1s, 2s
        
        errorLogger.logInfo(`Retrying authentication in ${delay}ms`, 'auth', {
          attempt: retryCountRef.current + 1,
          delay
        });
        
        setTimeout(() => {
          retryCountRef.current++;
          authenticationInProgressRef.current = false; // Reset flag before retry
          authenticate();
        }, delay);
      } else {
        errorLogger.logError(new Error('Max authentication retries exceeded'), 'auth', {
          totalAttempts: retryCountRef.current + 1
        });
        authenticationInProgressRef.current = false; // Reset flag if no more retries
      }
    }
  }, []);

  const handleRetry = useCallback(() => {
    errorLogger.logUserAction('auth-manual-retry', {
      previousAttempts: retryCountRef.current
    });
    
    clearAuthData(false); // Clear everything including originalUrl for manual retry
    retryCountRef.current = 0;
    authenticationInProgressRef.current = false; // Reset flag
    authenticate();
  }, [authenticate]);

  useEffect(() => {
    const path = window.location.pathname.split('/');
    if (path[1] !== 'syllabus') {
      // Only start authentication if it's not already in progress
      if (!authenticationInProgressRef.current) {
        authenticate();
      }
    } else {
      dispatch({ type: 'SUCCESS', user: null });
    }
  }, [authenticate]);

  if (state.loading) {
    return <Spinner />;
  }

  if (state.error) {
    return (
      <div className="alert alert-danger" role="alert">
        {state.error}
        {retryCountRef.current < 3 && <div>Auto-retrying...</div>}
        <button onClick={handleRetry} className="btn btn-link">
          Retry
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

const saveUserDetails = (userDetails) => {
  //Set session
  localStorage.setItem(SS_USER, JSON.stringify(userDetails));
};

export const loadUserDetails = () => {
  let userDetails = localStorage.getItem(SS_USER);
  return JSON.parse(userDetails);
};

const setHeader = (details) => {
  localStorage.setItem(SS_USER_NAME, details.personDetails.names.PRI.givenName);
};

function sha256base64url(message) {
  return sjcl.codec.base64url.fromBits(sjcl.hash.sha256.hash(message));
}

function generateRandomString(alphabet, length) {
  var randArr = new Uint8Array(length);
  var ret = '';
  window.crypto.getRandomValues(randArr);
  for (var i = 0; i < randArr.length; i++) {
    var j = randArr[i] % 64;
    ret += alphabet.substring(j, j + 1);
  }
  return ret;
}

function generateCodeVerifier() {
  var alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-.';
  var length = 128;
  return generateRandomString(alphabet, length);
}

function generateRandomServiceauthState() {
  var alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-.';
  var length = 64;
  return generateRandomString(alphabet, length);
}

const redirectToServiceAuth = () => {
  const codeVerifier = generateCodeVerifier();
  const state = generateRandomServiceauthState();

  // Store code verifier and state for PKCE
  localStorage.setItem(SS_SA_CODE_VERIFIER, codeVerifier);
  localStorage.setItem(SS_SA_STATE, state);

  let url = serviceauthOauthUrl;
  url += '?response_type=code';
  url += '&client_id=' + encodeURIComponent(serviceauthId);
  url += '&redirect_uri=' + encodeURIComponent(serviceauthRedirectUri);
  url += '&state=' + encodeURIComponent(state);
  url += '&code_challenge=' + encodeURIComponent(codeVerifier); // Should use codeVerifier for plain, or codeChallenge for S256
  url += '&code_challenge_method=plain';
  url += '&scope=' + encodeURIComponent(scopes.join(' '));

  return url;
};

const requestTokens = (code, state) => {
  return new Promise(async (resolve, reject) => {
    const params = new URLSearchParams();
    params.append('grant_type', 'authorization_code');
    params.append('client_id', serviceauthId);
    params.append('redirect_uri', serviceauthRedirectUri);
    params.append('code', code);
    params.append('code_verifier', localStorage.getItem(SS_SA_CODE_VERIFIER));
    params.append('state', state);
    params.append('scope', scopes.join(' '));
    try {
      const result = await axios({
        method: 'post',
        url: serviceauthTokenUrl,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: params,
      });
      resolve(result.data);
    } catch (error) {
      reject(error);
    }
  });
};

const requestTokenRefresh = () => {
  return new Promise(async (resolve, reject) => {
    //send the request
    const params = new URLSearchParams();
    params.append('grant_type', 'refresh_token');
    params.append('client_id', serviceauthId);
    params.append('refresh_token', getRefreshToken());
    params.append('scope', scopes.join(' '));

    try {
      const result = await axios({
        method: 'post',
        url: serviceauthRefreshTokenUrl,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        data: params,
      });

      resolve(result.data);
    } catch (error) {
      if (error.response && error.response.status === 400) {
        // Save current URL before re-authenticating if not already saved
        if (!localStorage.getItem('originalUrl')) {
          localStorage.setItem('originalUrl', window.location.href);
        }
        reauth();
      } else {
        reject(error);
      }
    }
  });
};

const saveToken = (token) => {
  localStorage.setItem(SS_JWT_TOKEN, token);
};

const saveTokenWithExpiration = (token) => {
  localStorage.setItem(SS_JWT_TOKEN, token);
  const expirationTime = new Date(
    new Date().getTime() + TOKEN_EXPIRES_IN_SECONDS * 1000
  );
  localStorage.setItem(SS_JWT_EXPIRATION, expirationTime.toISOString());
};

const saveRefreshToken = (refresh_token) => {
  localStorage.setItem(SS_JWT_REFRESH_TOKEN, refresh_token);
};

// Synchronous version for immediate token access (used by Api.js)
export const getToken = () => {
  let token = localStorage.getItem(SS_JWT_TOKEN);
  const tokenExpirationDate = localStorage.getItem(SS_JWT_EXPIRATION);

  if (!token) {
    return null;
  }

  // If no expiration date is stored, treat token as expired
  if (!tokenExpirationDate) {
    localStorage.removeItem(SS_JWT_TOKEN);
    return null;
  }

  // Check if token is expired
  const currentDate = new Date();
  const expirationDate = new Date(tokenExpirationDate);

  if (currentDate >= expirationDate) {
    // Token expired, remove it - refresh will be handled by makeAuthenticatedRequest
    localStorage.removeItem(SS_JWT_TOKEN);
    localStorage.removeItem(SS_JWT_EXPIRATION);
    return null;
  }

  return token;
};

// Async version that handles token refresh
export const getTokenAsync = async () => {
  let token = getToken();

  if (!token) {
    // Try to refresh if we have a refresh token
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        const { access_token, refresh_token } = await requestTokenRefresh();
        if (access_token) {
          saveTokenWithExpiration(access_token);

          if (refresh_token) {
            saveRefreshToken(refresh_token);
          }

          return access_token;
        }
      } catch (error) {
        clearAuthData(false); // Clear everything on token refresh failure
        return null;
      }
    }
    return null;
  }

  return token;
};

export const getNewToken = async (callback) => {
  try {
    const { access_token, refresh_token } = await requestTokenRefresh();
    if (access_token) {
      saveTokenWithExpiration(access_token);

      if (refresh_token) {
        saveRefreshToken(refresh_token);
      }

      if (callback && typeof callback === 'function') {
        callback();
      }
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

const getRefreshToken = () => {
  let refreshToken = localStorage.getItem(SS_JWT_REFRESH_TOKEN);
  return refreshToken;
};

export const reauth = () => {
  // Save the current URL before clearing auth data (unless already saved)
  const existingOriginalUrl = localStorage.getItem('originalUrl');
  if (!existingOriginalUrl) {
    localStorage.setItem('originalUrl', window.location.href);
  }

  clearAuthData(true); // Preserve originalUrl
  window.location.replace(redirectToServiceAuth());
};

export const authConstants = {
  SS_JWT_TOKEN: SS_JWT_TOKEN,
  SS_USER_NAME: SS_USER_NAME,
  LOGOUT_URL: LOGOUT_URL,
};
